import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  getEmailSchedules,
  createEmailSchedule,
  updateEmailSchedule,
  deleteEmailSchedule,
  toggleEmailSchedule,
  runEmailScheduleNow,
  sendTestEmail,
  cloneEmailSchedule,
  getEmailScheduleNextRuns,
  getEmailSchedulePortcos,
  EmailSchedule,
  CreateEmailScheduleInput,
  SchedulePortcoOption,
} from "../utils/PowerBIApiCall";
import { isDemoMode } from "../utils/PowerBIApiCall";
import { Loading } from "../ui-components/Loading";
import { EmailPreviewModal } from "../ui-components/EmailPreviewModal";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CRON_PRESETS: { label: string; value: string }[] = [
  { label: "Weekdays at 8:00 AM", value: "0 8 * * 1-5" },
  { label: "Weekdays at 6:00 AM", value: "0 6 * * 1-5" },
  { label: "Weekdays at 7:00 AM", value: "0 7 * * 1-5" },
  { label: "Weekdays at 9:00 AM", value: "0 9 * * 1-5" },
  { label: "Daily at 8:00 AM", value: "0 8 * * *" },
  { label: "Every Monday at 8:00 AM", value: "0 8 * * 1" },
  { label: "Every Monday at 9:00 AM", value: "0 9 * * 1" },
  { label: "Custom", value: "custom" },
];

const TIMEZONE_OPTIONS = [
  { label: "Eastern (America/New_York)", value: "America/New_York" },
  { label: "Central (America/Chicago)", value: "America/Chicago" },
  { label: "Mountain (America/Denver)", value: "America/Denver" },
  { label: "Pacific (America/Los_Angeles)", value: "America/Los_Angeles" },
  { label: "UTC", value: "UTC" },
];

function cronToHuman(expr: string): string {
  const preset = CRON_PRESETS.find((p) => p.value === expr);
  if (preset) return preset.label;

  const parts = expr.split(" ");
  if (parts.length !== 5) return expr;
  const [min, hour, , , dow] = parts;

  const hourNum = parseInt(hour, 10);
  const ampm = hourNum >= 12 ? "PM" : "AM";
  const h12 = hourNum % 12 || 12;
  const timeStr = `${h12}:${min.padStart(2, "0")} ${ampm}`;

  const dayMap: Record<string, string> = {
    "*": "Daily",
    "1-5": "Weekdays",
    "1": "Mon",
    "2": "Tue",
    "3": "Wed",
    "4": "Thu",
    "5": "Fri",
    "0,6": "Weekends",
  };
  const dayStr = dayMap[dow] || `Days: ${dow}`;

  return `${dayStr} at ${timeStr}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return "past";
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(hours / 24);
  return `in ${days}d`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusBadge(status?: string) {
  if (!status) return null;
  const colors: Record<string, string> = {
    success: "bg-green-50 text-green-700 border-green-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    skipped: "bg-yellow-50 text-yellow-700 border-yellow-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colors[status] || "bg-gray-50 text-gray-600 border-gray-200"}`}
    >
      {status}
    </span>
  );
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ---------------------------------------------------------------------------
// Schedule Form Modal
// ---------------------------------------------------------------------------

interface ScheduleFormProps {
  schedule?: EmailSchedule | null;
  portcos: SchedulePortcoOption[];
  onSave: (input: CreateEmailScheduleInput) => Promise<void>;
  onClose: () => void;
}

function ScheduleFormModal({
  schedule,
  portcos,
  onSave,
  onClose,
}: ScheduleFormProps) {
  const [name, setName] = useState(schedule?.name || "");
  const [portcoId, setPortcoId] = useState(
    schedule?.portcoId || (portcos[0]?.id ?? ""),
  );
  const [recipients, setRecipients] = useState(
    schedule?.recipients.join(", ") || "",
  );
  const [cronPreset, setCronPreset] = useState(() => {
    const existing = schedule?.cronExpression;
    if (!existing) return "0 8 * * 1-5";
    const match = CRON_PRESETS.find((p) => p.value === existing);
    return match ? existing : "custom";
  });
  const [customCron, setCustomCron] = useState(schedule?.cronExpression || "");
  const [timezone, setTimezone] = useState(
    schedule?.timezone || "America/New_York",
  );
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);
  const [includeAiSummary, setIncludeAiSummary] = useState(
    schedule?.includeAiSummary ?? false,
  );
  const [calloutMessage, setCalloutMessage] = useState(
    schedule?.calloutMessage || "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextRuns, setNextRuns] = useState<string[]>([]);

  const cronExpression = cronPreset === "custom" ? customCron : cronPreset;

  // Compute preview next runs locally
  useEffect(() => {
    if (schedule?.id && cronExpression) {
      getEmailScheduleNextRuns(schedule.id)
        .then((data) => setNextRuns(data.nextRuns))
        .catch(() => setNextRuns([]));
    } else {
      setNextRuns([]);
    }
  }, [schedule?.id, cronExpression]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const recipientList = recipients
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);

    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (recipientList.length === 0) {
      setError("At least one recipient is required");
      return;
    }
    const invalid = recipientList.find((r) => !isValidEmail(r));
    if (invalid) {
      setError(`Invalid email address: ${invalid}`);
      return;
    }
    if (!cronExpression.trim()) {
      setError("Cron expression is required");
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        portcoId,
        recipients: recipientList,
        cronExpression,
        timezone,
        enabled,
        includeAiSummary,
        calloutMessage: calloutMessage.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 flex items-start justify-center pt-16">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {schedule ? "Edit Schedule" : "Create Schedule"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ARG Daily Report"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
            />
          </div>

          {/* Portfolio Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Portfolio Company
            </label>
            <select
              value={portcoId}
              onChange={(e) => setPortcoId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
            >
              {portcos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipients
              <span className="text-gray-400 font-normal ml-1">
                (comma-separated)
              </span>
            </label>
            <textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="alice@company.com, bob@company.com"
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
            />
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule
            </label>
            <select
              value={cronPreset}
              onChange={(e) => {
                setCronPreset(e.target.value);
                if (e.target.value !== "custom") setCustomCron(e.target.value);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
            >
              {CRON_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            {cronPreset === "custom" && (
              <input
                type="text"
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                placeholder="0 8 * * 1-5"
                className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
              />
            )}
            {cronExpression && (
              <p className="mt-1 text-xs text-gray-500">
                {cronToHuman(cronExpression)}
              </p>
            )}
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Callout Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Callout Message
              <span className="text-gray-400 font-normal ml-1">
                (optional — shown as a highlighted note in the email)
              </span>
            </label>
            <textarea
              value={calloutMessage}
              onChange={(e) => setCalloutMessage(e.target.value)}
              placeholder="e.g. Missing sales data for West region today"
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent"
            />
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded border-gray-300 text-wmp-500 focus:ring-wmp-500"
              />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAiSummary}
                onChange={(e) => setIncludeAiSummary(e.target.checked)}
                className="rounded border-gray-300 text-wmp-500 focus:ring-wmp-500"
              />
              <span className="text-sm text-gray-700">Include AI Summary</span>
            </label>
          </div>

          {/* Next Runs Preview */}
          {nextRuns.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-500 mb-1">
                Next Scheduled Runs
              </p>
              <div className="space-y-0.5">
                {nextRuns.slice(0, 5).map((run, i) => (
                  <p key={i} className="text-xs text-gray-600">
                    {formatDateTime(run)}{" "}
                    <span className="text-gray-400">({timeUntil(run)})</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-wmp-500 rounded-lg hover:bg-wmp-600 disabled:opacity-50"
            >
              {saving ? "Saving..." : schedule ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Next Runs Panel
// ---------------------------------------------------------------------------

function NextRunsPanel({
  scheduleId,
  onClose,
}: {
  scheduleId: string;
  onClose: () => void;
}) {
  const [runs, setRuns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEmailScheduleNextRuns(scheduleId)
      .then((data) => setRuns(data.nextRuns))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [scheduleId]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 flex items-start justify-center pt-24">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            Upcoming Executions
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            &times;
          </button>
        </div>
        <div className="px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-gray-500">No upcoming runs</p>
          ) : (
            <div className="space-y-2">
              {runs.map((run, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-700">{formatDateTime(run)}</span>
                  <span className="text-gray-400 text-xs">
                    {timeUntil(run)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete Confirmation Modal
// ---------------------------------------------------------------------------

function DeleteConfirmModal({
  scheduleName,
  onConfirm,
  onCancel,
}: {
  scheduleName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Delete Schedule
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to delete &ldquo;{scheduleName}&rdquo;? This
          action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send Test Email Modal
// ---------------------------------------------------------------------------

function SendTestEmailModal({
  schedule,
  onClose,
  onSent,
}: {
  schedule: EmailSchedule;
  onClose: () => void;
  onSent: (msg: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!email.trim() || !isValidEmail(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const result = await sendTestEmail(schedule.id, email.trim());
      onSent(result.message);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send test email",
      );
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Send Test Email
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Send a test of &ldquo;{schedule.name}&rdquo; to a single address.
        </p>
        {error && (
          <div className="mb-3 bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200">
            {error}
          </div>
        )}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500 focus:border-transparent mb-4"
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-2 text-sm text-white bg-wmp-500 rounded-lg hover:bg-wmp-600 disabled:opacity-50"
          >
            {sending ? "Sending..." : "Send Test"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Content
// ---------------------------------------------------------------------------

function EmailSchedulesContent() {
  const [schedules, setSchedules] = useState<EmailSchedule[]>([]);
  const [activeTaskCount, setActiveTaskCount] = useState(0);
  const [portcos, setPortcos] = useState<SchedulePortcoOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editSchedule, setEditSchedule] = useState<EmailSchedule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailSchedule | null>(null);
  const [nextRunsTarget, setNextRunsTarget] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [previewSchedule, setPreviewSchedule] = useState<EmailSchedule | null>(
    null,
  );
  const [testEmailTarget, setTestEmailTarget] = useState<EmailSchedule | null>(
    null,
  );

  const loadData = useCallback(async () => {
    try {
      const [schedData, portcoData] = await Promise.all([
        getEmailSchedules(),
        getEmailSchedulePortcos(),
      ]);
      setSchedules(schedData.schedules);
      setActiveTaskCount(schedData.activeTaskCount);
      setPortcos(portcoData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (input: CreateEmailScheduleInput) => {
    const newSchedule = await createEmailSchedule(input);
    setSchedules((prev) => [...prev, newSchedule]);
    setShowForm(false);
    setStatusMsg("Schedule created successfully");
    setTimeout(() => setStatusMsg(null), 3000);
    loadData();
  };

  const handleUpdate = async (input: CreateEmailScheduleInput) => {
    if (!editSchedule) return;
    const updated = await updateEmailSchedule(editSchedule.id, input);
    setSchedules((prev) =>
      prev.map((s) => (s.id === updated.id ? updated : s)),
    );
    setEditSchedule(null);
    setShowForm(false);
    setStatusMsg("Schedule updated successfully");
    setTimeout(() => setStatusMsg(null), 3000);
    loadData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteEmailSchedule(deleteTarget.id);
    setSchedules((prev) => prev.filter((s) => s.id !== deleteTarget.id));
    setDeleteTarget(null);
    setStatusMsg("Schedule deleted");
    setTimeout(() => setStatusMsg(null), 3000);
    loadData();
  };

  const handleToggle = async (id: string) => {
    try {
      const updated = await toggleEmailSchedule(id);
      setSchedules((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s)),
      );
    } catch (err) {
      setStatusMsg(
        `Toggle failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
      setTimeout(() => setStatusMsg(null), 3000);
    }
  };

  const handleRunNow = async (id: string) => {
    setRunningId(id);
    try {
      const result = await runEmailScheduleNow(id);
      setStatusMsg(result.message);
      setTimeout(() => setStatusMsg(null), 4000);
      loadData();
    } catch (err) {
      setStatusMsg(
        `Run failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
      setTimeout(() => setStatusMsg(null), 4000);
    } finally {
      setRunningId(null);
    }
  };

  const handleClone = async (id: string) => {
    try {
      const clone = await cloneEmailSchedule(id);
      setSchedules((prev) => [...prev, clone]);
      setStatusMsg(`Schedule cloned as "${clone.name}"`);
      setTimeout(() => setStatusMsg(null), 3000);
      loadData();
    } catch (err) {
      setStatusMsg(
        `Clone failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
      setTimeout(() => setStatusMsg(null), 4000);
    }
  };

  const getPortcoName = (portcoId: string) => {
    const p = portcos.find((pc) => pc.id === portcoId);
    return p?.displayName || portcoId;
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="bg-red-50 text-red-700 rounded-lg p-4">{error}</div>
      </div>
    );
  }

  const enabledCount = schedules.filter((s) => s.enabled).length;
  const totalRecipients = new Set(schedules.flatMap((s) => s.recipients)).size;

  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-wmp-500 hover:text-wmp-600 text-sm"
            >
              &larr; Admin
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              Email Schedules
            </h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Manage automated email report schedules
            <span className="mx-1.5 text-gray-300">|</span>
            <Link
              to="/admin/email-history"
              className="text-wmp-500 hover:text-wmp-600"
            >
              History
            </Link>
            <span className="mx-1.5 text-gray-300">|</span>
            <Link
              to="/admin/email-templates"
              className="text-wmp-500 hover:text-wmp-600"
            >
              Templates
            </Link>
          </p>
        </div>
        <button
          onClick={() => {
            setEditSchedule(null);
            setShowForm(true);
          }}
          className="px-4 py-2 text-sm text-white bg-wmp-500 rounded-lg hover:bg-wmp-600 font-medium"
        >
          + Create Schedule
        </button>
      </div>

      {/* Status message */}
      {statusMsg && (
        <div className="mb-4 bg-blue-50 text-blue-700 text-sm px-4 py-2 rounded-lg border border-blue-200">
          {statusMsg}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Total Schedules
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {schedules.length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Active
          </p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {enabledCount}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Running Tasks
          </p>
          <p className="text-2xl font-bold text-wmp-500 mt-1">
            {activeTaskCount}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Unique Recipients
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {totalRecipients}
          </p>
        </div>
      </div>

      {/* Schedule list */}
      {schedules.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-500 text-lg mb-2">No schedules yet</p>
          <p className="text-gray-400 text-sm mb-4">
            Create your first email schedule to get started
          </p>
          <button
            onClick={() => {
              setEditSchedule(null);
              setShowForm(true);
            }}
            className="px-4 py-2 text-sm text-white bg-wmp-500 rounded-lg hover:bg-wmp-600"
          >
            + Create Schedule
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Portfolio Co.
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Recipients
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Schedule
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Last Run
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schedules.map((schedule) => (
                  <tr key={schedule.id} className="hover:bg-gray-50">
                    {/* Status dot */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggle(schedule.id)}
                        title={
                          schedule.enabled
                            ? "Click to disable"
                            : "Click to enable"
                        }
                        className="group"
                      >
                        <span
                          className={`inline-block w-3 h-3 rounded-full ${
                            schedule.enabled
                              ? "bg-green-500 group-hover:bg-green-600"
                              : "bg-gray-300 group-hover:bg-gray-400"
                          }`}
                        />
                      </button>
                    </td>

                    {/* Name */}
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {schedule.name}
                      </span>
                      {schedule.includeAiSummary && (
                        <span className="ml-2 text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                          AI
                        </span>
                      )}
                    </td>

                    {/* Portco */}
                    <td className="px-4 py-3 text-gray-600">
                      {getPortcoName(schedule.portcoId)}
                    </td>

                    {/* Recipients */}
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs cursor-help"
                        title={schedule.recipients.join("\n")}
                      >
                        {schedule.recipients.length}{" "}
                        {schedule.recipients.length === 1
                          ? "recipient"
                          : "recipients"}
                      </span>
                    </td>

                    {/* Schedule */}
                    <td className="px-4 py-3">
                      <span className="text-gray-700 text-xs">
                        {cronToHuman(schedule.cronExpression)}
                      </span>
                      <br />
                      <span className="text-gray-400 text-xs">
                        {schedule.timezone.replace("America/", "")}
                      </span>
                    </td>

                    {/* Last Run */}
                    <td className="px-4 py-3">
                      {schedule.lastRunAt ? (
                        <div className="space-y-0.5">
                          <div>{statusBadge(schedule.lastRunStatus)}</div>
                          <p className="text-xs text-gray-400">
                            {timeAgo(schedule.lastRunAt)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Never</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setPreviewSchedule(schedule)}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-wmp-500 hover:bg-wmp-50 rounded"
                          title="Preview email"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => setNextRunsTarget(schedule.id)}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-wmp-500 hover:bg-wmp-50 rounded"
                          title="View upcoming runs"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => setTestEmailTarget(schedule)}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded"
                          title="Send test email"
                        >
                          Test
                        </button>
                        <button
                          onClick={() => handleRunNow(schedule.id)}
                          disabled={runningId === schedule.id}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                          title="Run now"
                        >
                          {runningId === schedule.id ? "..." : "Run"}
                        </button>
                        <button
                          onClick={() => handleClone(schedule.id)}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-wmp-500 hover:bg-wmp-50 rounded"
                          title="Clone schedule"
                        >
                          Clone
                        </button>
                        <button
                          onClick={() => {
                            setEditSchedule(schedule);
                            setShowForm(true);
                          }}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-wmp-500 hover:bg-wmp-50 rounded"
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(schedule)}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          Del
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <ScheduleFormModal
          schedule={editSchedule}
          portcos={portcos}
          onSave={editSchedule ? handleUpdate : handleCreate}
          onClose={() => {
            setShowForm(false);
            setEditSchedule(null);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          scheduleName={deleteTarget.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {nextRunsTarget && (
        <NextRunsPanel
          scheduleId={nextRunsTarget}
          onClose={() => setNextRunsTarget(null)}
        />
      )}

      {previewSchedule && (
        <EmailPreviewModal
          portcoId={previewSchedule.portcoId}
          calloutMessage={previewSchedule.calloutMessage}
          onClose={() => setPreviewSchedule(null)}
        />
      )}

      {testEmailTarget && (
        <SendTestEmailModal
          schedule={testEmailTarget}
          onClose={() => setTestEmailTarget(null)}
          onSent={(msg) => {
            setStatusMsg(msg);
            setTimeout(() => setStatusMsg(null), 4000);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function EmailSchedules() {
  const { isAdmin, loading } = useIsAdmin();

  if (loading) return <Loading />;

  // Demo mode: always show (useIsAdmin returns true for demo)
  if (!isAdmin && !isDemoMode()) return <Navigate to="/dashboard" replace />;

  return <EmailSchedulesContent />;
}
